import http from "@/services/http";
import { onPatientDataChanged } from "@/services/patientDataBus";

/**
 * Store do dashboard do paciente — carrega TODAS as seções dos cards num único
 * request (endpoint agregado) e serve cada card a partir desse snapshot.
 *
 * Isso substitui a rajada de ~20 GETs (1 por card) por 1 só — tanto no load
 * quanto no "atualizou um → atualiza todos" (o barramento invalida o snapshot;
 * a próxima onda recarrega o agregado UMA vez e todos os cards releem). A
 * semântica de sincronização é preservada.
 *
 * Segurança / zero mudança de comportamento: `section(id, key, fallback)`
 * devolve a seção do agregado quando existe; se o agregado não trouxe a seção
 * (ou falhou), chama `fallback()` — o fetch original do card. Qualquer card não
 * coberto (ou falha do agregado) continua funcionando exatamente como antes.
 *
 * Correção de corrida: usamos uma "geração" que incrementa a cada mudança de
 * dados. Cada carga é marcada com a geração desejada e só é comitada se ainda
 * for a mais recente. O request do agregado leva `?_=<geração>` para nunca
 * reaproveitar (via dedup do http.js) uma resposta em voo de uma onda anterior.
 */

let patientId = null;
let snapshot = null; // { key: data } | null
let snapshotGen = -1; // geração do snapshot atual
let wantedGen = 0; // geração desejada (incrementa quando dados mudam)
let inflight = null; // { gen, promise } | null

function reset(id) {
  patientId = id;
  snapshot = null;
  snapshotGen = -1;
  wantedGen = 0;
  inflight = null;
}

function loadAggregate(id, gen) {
  return http
    .get(`/api/patients/${id}/dashboard`, { params: { _: gen } })
    .then((res) => res.data?.data ?? res.data ?? {})
    .catch(() => ({})); // falha → snapshot vazio → cards caem no fallback
}

// Uma única inscrição no barramento: qualquer mudança "vence" o snapshot atual,
// forçando a próxima leitura a recarregar o agregado.
onPatientDataChanged(() => {
  wantedGen += 1;
});

/** Garante um snapshot da geração desejada (coalescendo cargas concorrentes). */
function ensureSnapshot(id) {
  if (snapshot && snapshotGen === wantedGen) return Promise.resolve(snapshot);
  if (inflight && inflight.gen === wantedGen) return inflight.promise;

  const gen = wantedGen;
  const promise = loadAggregate(id, gen).then((snap) => {
    // Só comita se ainda for a geração mais recente (evita sobrescrever com dado velho).
    if (gen === wantedGen) {
      snapshot = snap;
      snapshotGen = gen;
    }
    // Libera o slot apenas se ainda for a carga desta geração.
    if (inflight && inflight.gen === gen) inflight = null;
    return snap;
  });

  inflight = { gen, promise };
  return promise;
}

export default {
  /**
   * Devolve a seção `key` do agregado; se ausente, usa `fallback()`.
   * @param {number|string} id patientId
   * @param {string} key seção (ex.: "allergies")
   * @param {Function} fallback () => Promise<data> — fetch original do card
   */
  async section(id, key, fallback) {
    if (id == null) {
      return typeof fallback === "function" ? fallback() : null;
    }
    if (String(id) !== String(patientId)) reset(id);

    let snap;
    try {
      snap = await ensureSnapshot(id);
    } catch {
      snap = {};
    }

    if (snap && Object.prototype.hasOwnProperty.call(snap, key)) {
      return snap[key];
    }
    return typeof fallback === "function" ? await fallback() : null;
  },
};
